import { PointMaterial } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import React, {
  useEffect,
  useRef,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { Vector3 } from "three";
import { AppContext } from "./App";
import angleBetweenPoints from "./utility_functions/AngleBetweenPoints";
import { useLoader } from "@react-three/fiber";
import { TextureLoader } from "three/src/loaders/TextureLoader";

export default function Player() {
  const lightRef = useRef<any>();
  const {
    keysRef,
    worldSize,
    playerRef,
    playerSize,
    playerSpeed,
    cameraRef,
    wallOpacityRef,
    startPosition,

    hasStarted,
  } = useContext(AppContext);
  const maxSpeed = 0.15;
  const minReleaseSpeed = 0.04;
  const dragFactor = 0.99;
  const accSpeed = 0.012;
  const touchAccAngle = useRef<number | null>(null);
  const texture = useLoader(TextureLoader, "/star.png");
  const pulseFactor = 0.3;
  const pulseSpeed = 0.0015;
  //const [pulseSize, setPulseSize] = useState(1);

  useEffect(() => {
    function startTouch(e: TouchEvent) {
      e.preventDefault();
      const projectionVector = new Vector3(...playerRef.current.position);
      projectionVector.project(cameraRef.current);
      projectionVector.x = ((projectionVector.x + 1) * window.innerWidth) / 2;
      projectionVector.y = (-(projectionVector.y - 1) * window.innerHeight) / 2;
      projectionVector.z = 0;

      touchAccAngle.current = angleBetweenPoints(
        { x: projectionVector.x, y: projectionVector.y },
        { x: e.touches[0].clientX, y: e.touches[0].clientY }
      );
    }

    function endTouch(e: TouchEvent) {
      touchAccAngle.current = null;
    }

    window.addEventListener("touchstart", startTouch);
    window.addEventListener("touchmove", startTouch);
    window.addEventListener("touchend", endTouch);

    playerRef.current.position.set(startPosition.x, 0, startPosition.z);

    return () => {
      window.removeEventListener("touchstart", startTouch);
      window.removeEventListener("touchmove", startTouch);

      window.removeEventListener("touchend", endTouch);
    };
  }, []);

  useFrame(() => {
    if (hasStarted) {
      if (keysRef.current.ArrowUp) {
        if (playerSpeed.current.z + accSpeed <= maxSpeed)
          playerSpeed.current.z += accSpeed;
      }
      if (keysRef.current.ArrowDown) {
        if (playerSpeed.current.z - accSpeed >= -maxSpeed)
          playerSpeed.current.z -= accSpeed;
      }
      if (keysRef.current.ArrowLeft) {
        if (playerSpeed.current.x + accSpeed <= maxSpeed)
          playerSpeed.current.x += accSpeed;
      }
      if (keysRef.current.ArrowRight) {
        if (playerSpeed.current.x - accSpeed >= -maxSpeed)
          playerSpeed.current.x -= accSpeed;
      }

      if (touchAccAngle.current !== null) {
        playerSpeed.current.x = Math.min(
          maxSpeed,
          Math.max(
            -maxSpeed,
            playerSpeed.current.x - Math.cos(touchAccAngle.current) * accSpeed
          )
        );
      }
      if (touchAccAngle.current !== null) {
        playerSpeed.current.z = Math.min(
          maxSpeed,
          Math.max(
            -maxSpeed,
            playerSpeed.current.z - Math.sin(touchAccAngle.current) * accSpeed
          )
        );
      }

      if (
        !keysRef.current.ArrowUp &&
        !keysRef.current.ArrowDown &&
        !keysRef.current.ArrowLeft &&
        !keysRef.current.ArrowRight &&
        touchAccAngle.current === null
      ) {
        if (
          Math.abs(playerSpeed.current.x) > minReleaseSpeed ||
          Math.abs(playerSpeed.current.z) > minReleaseSpeed
        ) {
          playerSpeed.current.x *= dragFactor;
          playerSpeed.current.z *= dragFactor;
        }
      }

      if (playerRef.current) {
        const newPos = [
          playerRef.current.position.x - playerSpeed.current.x,
          playerRef.current.position.y,
          playerRef.current.position.z - playerSpeed.current.z,
        ];
        if (newPos[0] + playerSize / 2 > worldSize.x / 2) {
          newPos[0] = worldSize.x / 2 - playerSize / 2;
          playerSpeed.current.x = -playerSpeed.current.x;
          wallOpacityRef.current = 1;
        }
        if (newPos[0] - playerSize / 2 < -worldSize.x / 2) {
          newPos[0] = -worldSize.x / 2 + playerSize / 2;
          playerSpeed.current.x = -playerSpeed.current.x;
          wallOpacityRef.current = 1;
        }
        if (newPos[2] + playerSize / 2 > worldSize.z / 2) {
          newPos[2] = worldSize.z / 2 - playerSize / 2;
          playerSpeed.current.z = -playerSpeed.current.z;
          wallOpacityRef.current = 1;
        }
        if (newPos[2] - playerSize / 2 < -worldSize.z / 2) {
          newPos[2] = -worldSize.z / 2 + playerSize / 2;
          playerSpeed.current.z = -playerSpeed.current.z;
          wallOpacityRef.current = 1;
        }
        playerRef.current.position.set(newPos[0], newPos[1], newPos[2]);
        lightRef.current?.position?.set(newPos[0], newPos[1] + 1, newPos[2]);

        const pulseSize =
          Math.sin(performance.now() * pulseSpeed) * pulseFactor;

        playerRef.current.scale.set(
          playerSize + pulseSize,
          playerSize + pulseSize
        );
      }

      // setPulseSize(Math.sin(performance.now() * pulseSpeed) * pulseFactor);
    }
  });

  return (
    <>
      <sprite ref={playerRef} position={[0, 1, 0]} scale={playerSize}>
        <spriteMaterial map={texture} />
      </sprite>
    </>
  );
}
